import { AttachmentResponseDto } from '@issuemanager/api-client';
import {
  ControlledFileUploader,
  FileList,
} from '@issuemanager/apps/client/components';
import { IssuePermissionsEnum, IssueStatusEnum } from '@issuemanager/constants';
import { Stack, Typography } from '@mui/material';
import React from 'react';
import { Control, FieldValues, Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { EmptyWorkflowState } from '../../components/empty-workflow-state';
import { GroupedDocumentList } from '../../components/grouped-document-list';
import { useGroupedAttachments } from '../../hooks/use-grouped-attachments';
import { useIssueOutletContext } from '../../hooks/useIssueOutletContext';

type Props<TFieldValues extends FieldValues> = {
  control: Control<TFieldValues>;
  name: Path<TFieldValues>;
  isEditing: boolean;
  attachments: AttachmentResponseDto[];
  onEdit: () => void;
};

export function Documents<TFieldValues extends FieldValues>({
  control,
  name,
  isEditing,
  attachments,
  onEdit,
}: Props<TFieldValues>): React.JSX.Element {
  const { t } = useTranslation('issue');
  const { issue } = useIssueOutletContext();

  const isInProgress =
    issue.data?.status === IssueStatusEnum.IN_PROGRESS ||
    issue.data?.status === IssueStatusEnum.REMEDIATION_PLANNING ||
    issue.data?.status === IssueStatusEnum.IN_REMEDIATION;

  const canAddAttachments = Boolean(
    issue.data?.editableFields?.includes('attachments'),
  );

  const showFileUploader = isEditing && canAddAttachments;
  const showEmptyState = !attachments.length && canAddAttachments;
  const showReadOnlyEmptyState =
    !showFileUploader && !showEmptyState && !attachments.length;

  const { regularAttachments, approvalAttachments } =
    useGroupedAttachments(attachments);

  return (
    <Stack data-testid='documents' spacing={3}>
      <Typography variant='h3' fontWeight='medium'>
        {t('form.sections.documents')}
      </Typography>

      {showFileUploader && (
        <>
          <ControlledFileUploader
            control={control}
            name={name}
            fullWidth
            initialFileList={isInProgress ? [] : attachments}
            permissions={[
              IssuePermissionsEnum.IsIssueTypeManager,
              IssuePermissionsEnum.IsCreator,
              IssuePermissionsEnum.IsOwner,
            ]}
          />
          {isInProgress && (
            <FileList files={attachments} showVerticalDividers={false} />
          )}
        </>
      )}

      {!showFileUploader && showEmptyState && (
        <EmptyWorkflowState
          onEdit={onEdit}
          description={t('manage.documents.empty.description')}
        />
      )}

      {showReadOnlyEmptyState && (
        <EmptyWorkflowState
          description={t('manage.documents.empty.description')}
        />
      )}

      {!showFileUploader && !showEmptyState && !showReadOnlyEmptyState && (
        <GroupedDocumentList
          regularAttachments={regularAttachments}
          approvalAttachments={approvalAttachments}
          spacing={1}
        />
      )}
    </Stack>
  );
}
